import type { AxiosInstance } from 'axios'
import type { ICompany, IFactoryFunctionRequest, paginate } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'companys'

/**
 * @deprecated
 * Use filter instead
 */
const getFilter = (axios: IFactoryFunctionRequest<Array<Pick<ICompany, '_id' | 'display'>>, undefined>) => (options = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/filter`,
    query: options,
  })
}

const filter = (axios: IFactoryFunctionRequest<paginate<Array<Pick<ICompany, '_id' | 'display'>>>, undefined>) => (options = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`,
    query: options,
  })
}

const createOne = (axios: IFactoryFunctionRequest<Array<Pick<ICompany, '_id' | 'display'>>, undefined>) => (data) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    data,
  })
}

const updateOne = (axios: IFactoryFunctionRequest<ICompany, Partial<ICompany>>) => (companyId: string, data: Partial<ICompany>) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${companyId}`,
    data,
  })
}

const getFirmClients = (axios: IFactoryFunctionRequest<Array<ICompany>, undefined>) => (firmId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/${firmId}/1`,
  })
}

const getOneDetails = (axios: IFactoryFunctionRequest<ICompany, undefined>) => (clientId) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${clientId}/details`,
  })
}

const createNote = (axios: IFactoryFunctionRequest<{ headline: string; content: string; authorId: string }, undefined>) => (clientId, data) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${clientId}/createNote`,
    data,
  })
}

const editNote = (axios: IFactoryFunctionRequest<ICompany, undefined>) => (clientId, noteId, data) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${clientId}/editNote/${noteId}`,
    data,
  })
}

const getCompanies = (axios: IFactoryFunctionRequest<paginate<Array<ICompany>>, undefined>) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/getCompanies`,
    query,
  })
}

const getManagementCompanies = (axios: IFactoryFunctionRequest<paginate<Array<ICompany>>, undefined>) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/management/list`,
    query,
  })
}

const createManagementCompany = (axios: IFactoryFunctionRequest<ICompany, Partial<ICompany>>) => (data: Partial<ICompany> & { integrationExclusions?: string[] }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/management/create`,
    data,
  })
}

const createCompany = (axios: IFactoryFunctionRequest) => (body: Record<string, any>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/createCompany`,
    body
  })
}

const addDocuments = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, documents: Array<any>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/documents`,
    data: { documents },
  })
}

const deleteDocument = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, documentId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/documents/${documentId}`,
  })
}

const toggleDocumentPublicStatus = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, documentId: string, isPublic: boolean) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/documents/${documentId}/togglePublicStatus`,
    data: { isPublic },
  })
}

const moveDocument = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, fromIndex: number, toIndex: number) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/documents/move`,
    data: { fromIndex, toIndex },
  })
}

// Invoicing data atomic updates
const updateInvoicingData = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, data: Record<string, any>) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/invoicing`,
    data,
  })
}

const addInvoicingEntity = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, data: Record<string, any>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/invoicingEntities`,
    data,
  })
}

const updateInvoicingEntity = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, entityId: string, data: Record<string, any>) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/invoicingEntities/${entityId}`,
    data,
  })
}

const deleteInvoicingEntity = (axios: IFactoryFunctionRequest<any, any>) => (companyId: string, entityId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${companyId}/invoicingEntities/${entityId}`,
  })
}

const bulkCreateTemplate = (axios: IFactoryFunctionRequest<any, any>) => (firmId?: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/template`,
    query: firmId ? { firmId } : undefined,
  })
}

const bulkCreateValidate =
  (axios: IFactoryFunctionRequest<any, any>) => (file: File, firmId?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/validate`,
      query: firmId ? { firmId } : undefined,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

const bulkCreate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (rows: { data: Record<string, any>; resolvedData?: Record<string, any> }[], firmId?: string, integrationExclusions?: string[]) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create`,
      data: {
        rows,
        ...(firmId ? { firmId } : {}),
        // integrations the new clients must not be sent to
        ...(integrationExclusions?.length ? { integrationExclusions } : {}),
      },
    })
  }

export default (axios: AxiosInstance) => ({
  getFilter: getFilter(useAxios(axios)),
  filter: filter(useAxios(axios)),
  createOne: createOne(useAxios(axios)),
  updateOne: updateOne(useAxios(axios)),
  getFirmClients: getFirmClients(useAxios(axios)),
  getOneDetails: getOneDetails(useAxios(axios)),
  createNote: createNote(useAxios(axios)),
  editNote: editNote(useAxios(axios)),
  getCompanies: getCompanies(useAxios(axios)),
  getManagementCompanies: getManagementCompanies(useAxios(axios)),
  createManagementCompany: createManagementCompany(useAxios(axios)),
  createCompany: createCompany(useAxios(axios)),
  addDocuments: addDocuments(useAxios(axios)),
  deleteDocument: deleteDocument(useAxios(axios)),
  toggleDocumentPublicStatus: toggleDocumentPublicStatus(useAxios(axios)),
  moveDocument: moveDocument(useAxios(axios)),
  updateInvoicingData: updateInvoicingData(useAxios(axios)),
  addInvoicingEntity: addInvoicingEntity(useAxios(axios)),
  updateInvoicingEntity: updateInvoicingEntity(useAxios(axios)),
  deleteInvoicingEntity: deleteInvoicingEntity(useAxios(axios)),
  bulkCreateTemplate: bulkCreateTemplate(useAxios(axios)),
  bulkCreateValidate: bulkCreateValidate(useAxios(axios)),
  bulkCreate: bulkCreate(useAxios(axios)),
})
